import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";

const principles = [
  { icon: "ri-focus-3-line", title: "真实性", text: "所有游戏资讯与联动情报均基于官方公告与公开资料，不编造、不夸大，确保信息可追溯。" },
  { icon: "ri-speed-line", title: "及时性", text: "紧跟发布会与公告节奏，第一时间跟进新游漫画化、版本更新漫画与活动漫画内容。" },
  { icon: "ri-leaf-line", title: "克制性", text: "拒绝标题党与哗众取宠，用平实专业的语言呈现内容，把阅读价值放在首位。" },
  { icon: "ri-heart-3-line", title: "服务性", text: "站在玩家视角整理情报，帮助读者用更少时间掌握更多有效的游戏漫画信息。" },
];

const milestones = [
  { period: "创立初心", text: "见证游戏与漫画内容跨界融合的浪潮，我们希望做一个把游戏情报与漫画内容串联起来的专业站点。" },
  { period: "内容聚焦", text: "围绕游戏资讯、联动情报、新游漫画化、版本更新漫画四大方向持续深耕，形成差异化的内容定位。" },
  { period: "未来方向", text: "不断拓展联动情报的覆盖范围，优化阅读体验，让每一位关注游戏漫画内容的读者都能有所收获。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 可游漫画网 品牌介绍与发展理念",
    metaDesc:
      "了解可游漫画网：专注游戏资讯与联动情报，跟进新游漫画化、版本更新漫画及活动漫画内容。品牌定位、发展理念与内容原则一睹为快。",
  });

  return (
    <PageLayout>
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="manga-clip inline-flex items-center gap-2 bg-accent-500 px-3 py-1 text-sm font-bold text-foreground-950">
              <i className="ri-information-line" />关于可游漫画网
            </span>
            <h1 className="mt-5 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
              让游戏情报与漫画内容，一站掌握
            </h1>
            <p className="mt-5 text-base leading-relaxed text-foreground-700">
              可游漫画网是一个专注游戏资讯与联动情报的内容平台。我们跟进新游漫画化、版本更新漫画及活动漫画内容，
              将分散在官方公告、发布会与公开资料中的信息整理成清晰、易读的情报文章，帮助玩家第一时间掌握热门游戏漫画化动态与联动活动。
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              无论你是关注新游漫画化进展的剧情党，还是追踪跨IP联动活动的玩家，都能在这里找到实用的游戏情报与漫画解读。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link to="/youxi-zixun" className="manga-clip whitespace-nowrap bg-primary-500 px-6 py-3 text-center text-base font-bold text-background-50 hover:bg-primary-400">
                浏览游戏资讯
              </Link>
              <Link to="/contact" className="whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-center text-base font-bold text-foreground-700 hover:border-primary-400 hover:text-primary-600">
                联系编辑部
              </Link>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=creative%20editorial%20workspace%20for%20a%20gaming%20and%20manga%20news%20team%2C%20desk%20with%20laptop%20sketches%20and%20manga%20artwork%2C%20warm%20orange%20and%20gold%20lighting%2C%20cozy%20professional%20atmosphere%2C%20clean%20composition&width=1200&height=900&seq=keyou-about-01&orientation=portrait"
              alt="可游漫画网编辑团队工作场景"
              title="可游漫画网编辑团队工作场景"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading kicker="网站定位" title="我们专注的方向" align="center" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: "ri-gamepad-line", title: "游戏资讯", text: "热门游戏一手快讯与版本动态" },
              { icon: "ri-hand-heart-line", title: "联动情报", text: "游戏×动画、IP跨界合作追踪" },
              { icon: "ri-pen-nib-line", title: "新游漫画化", text: "游戏改编漫画企划与连载抢先看" },
              { icon: "ri-refresh-line", title: "版本更新", text: "版本更新漫画与活动内容速递" },
            ].map((f) => (
              <div key={f.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="manga-clip-both flex h-10 w-10 items-center justify-center bg-secondary-500 text-background-50">
                  <i className={`${f.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-base text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm text-foreground-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <SectionHeading kicker="发展理念" title="我们的坚持" />
            <div className="space-y-4">
              {milestones.map((m) => (
                <div key={m.period} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="manga-clip-both flex h-11 w-11 shrink-0 items-center justify-center bg-primary-500 font-heading text-sm text-background-50">
                    创
                  </span>
                  <div>
                    <h3 className="font-heading text-base text-foreground-950">{m.period}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading kicker="内容原则" title="我们的标准" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((p) => (
                <div key={p.title} className="rounded-lg bg-background-100 p-5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className={`${p.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 font-heading text-base text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <SectionHeading kicker="联系方式" title="找到我们" align="center" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-mail-line" />
              </span>
              <p className="mt-3 text-sm font-bold text-foreground-950">邮箱</p>
              <p className="mt-1 text-sm text-foreground-600">contact@keyoumanga.example</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                <i className="ri-wechat-line" />
              </span>
              <p className="mt-3 text-sm font-bold text-foreground-950">微信</p>
              <p className="mt-1 text-sm text-foreground-600">可游漫画网</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                <i className="ri-time-line" />
              </span>
              <p className="mt-3 text-sm font-bold text-foreground-950">服务时间</p>
              <p className="mt-1 text-sm text-foreground-600">每日 09:00 - 22:00</p>
            </div>
          </div>
        </div>
      </section>
      <BackToTop />
    </PageLayout>
  );
}